import {
  LockClosedIcon,
  TrashIcon as TrashIconOutline
} from '@heroicons/react/24/outline'
import { useQuery } from '@tanstack/react-query'
import { Buffer } from 'buffer'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from 'constants/constants'
import { ActivityIndicator } from '..'
import {
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../../context'
import { useFeatureGate } from '../../features/pricing/use-feature-gate'
import { mutation, query } from '../../graphql'
import { uuidv4 } from '../../helpers'
import LockBadge from '../pricing/lock-badge'
import SEO from '../seo'
import InfoCallout from '../tailwind/InfoCallout'
import ApiKey from './api-key'
import SettingsContainer from './settings-container'

const TRANSLATION_LOCATION = 'components.settings.api_keys.'

// Der Key authentifiziert auch die atsGateway-Anbindungen, deshalb ist das
// Gate bei API, INTEGRATIONS oder BROWSERPLUGIN frei (Katalog in plans.js)
const API_KEY_FEATURE = 'CUSTOM_API'
const API_KEY_SOURCE = 'settings_api_keys'

const ApiKeys = () => {
  const { t } = useTranslation()
  const { success, alert } = useNotificationContext()
  const { activeSpace } = useUserContext()
  const { partner } = usePartnerContext()

  const [isLoading, setIsLoading] = useState(false)

  const {
    fetchStatus,
    data: apiKey,
    refetch
  } = useQuery({
    queryKey: QUERY_KEYS.apiKeysByPartner(partner.id),
    queryFn: () =>
      query({
        query: 'apiKeysByPartner',
        variables: { partner_id: partner.id }
      }),
    select: (items) => items[0] || null
  })

  // Nur in enforced gesperrt. Ein bestehender Key bleibt sichtbar, kopier-
  // und löschbar, gesperrt ist nur das Erstellen eines neuen.
  const gate = useFeatureGate(API_KEY_FEATURE, {
    source: API_KEY_SOURCE,
    inUse: !!apiKey
  })

  const createApiKey = async () => {
    setIsLoading(true)

    const auth_token = 'Bearer ' + Buffer.from(uuidv4()).toString('base64')

    mutation({
      mutation: 'createApiKey',
      input: {
        auth_token,
        partner_id: partner.id,
        authorized_create: ['__create_' + partner.id],
        authorized_read: ['__read_' + partner.id],
        authorized_update: ['__update_' + partner.id],
        authorized_delete: ['__delete_' + partner.id],
        owner: activeSpace.owner
      }
    })
      .then(() => {
        refetch()
        success(t(TRANSLATION_LOCATION + 'create_api_key_success'))
        setIsLoading(false)
      })
      .catch(() => setIsLoading(false))
  }

  const deleteApiKey = async ({ id }) => {
    alert(
      t(TRANSLATION_LOCATION + 'modal_delete_api_key_title'),
      t(TRANSLATION_LOCATION + 'modal_delete_api_key_text'),
      [
        {
          onPress: async () => {
            setIsLoading(true)

            try {
              await mutation({ mutation: 'deleteApiKey', input: { id } })
            } catch (err) {
              setIsLoading(false)
              return
            }

            refetch()
            success(
              t(TRANSLATION_LOCATION + 'delete_api_key_success_description')
            )
            setIsLoading(false)
          },
          text: t(TRANSLATION_LOCATION + 'modal_delete_api_key_action')
        },
        {
          text: t(TRANSLATION_LOCATION + 'modal_cancel_action'),
          style: 'cancel'
        }
      ],
      <TrashIconOutline className='h-6 w-6' />
    )
  }

  const getApiKey = ({ apiKey }) => {
    const { auth_token } = apiKey

    return auth_token.replace('Bearer ', '')
  }

  const is_loading = fetchStatus === 'fetching' || isLoading

  return (
    <SettingsContainer
      title={t(TRANSLATION_LOCATION + 'settings_container_title')}
      description={t(TRANSLATION_LOCATION + 'settings_container_description')}
    >
      <SEO title={t('seo.titles.settings_ats')} />
      <div className='relative'>
        <legend className='text-base font-medium text-gray-900'>API-Key</legend>
        {apiKey && !is_loading && (
          <div className='mt-2'>
            <ApiKey
              apiKey={getApiKey({ apiKey })}
              deleteApiKey={() => deleteApiKey(apiKey)}
            />
            {gate.locked && (
              <InfoCallout
                tone='neutral'
                icon={LockClosedIcon}
                className='mt-2'
                action={
                  <LockBadge
                    feature={API_KEY_FEATURE}
                    source={API_KEY_SOURCE}
                    inUse
                  />
                }
              >
                {t('pricing.gates.api_keys_existing_hint')}
              </InfoCallout>
            )}
          </div>
        )}
        {!apiKey && !is_loading && gate.locked && (
          <div
            data-pricing-locked={API_KEY_FEATURE}
            className='mt-2 flex flex-col gap-3 rounded-lg border-2 border-dashed border-gray-200 p-4 sm:flex-row sm:items-center'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-8 w-8 shrink-0 text-gray-300'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              aria-hidden='true'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4'
              />
            </svg>
            <p className='min-w-0 flex-1 text-sm text-gray-600'>
              {t('pricing.gates.api_keys_create_locked_hint')}
            </p>
            <LockBadge
              feature={API_KEY_FEATURE}
              source={API_KEY_SOURCE}
              size='sm'
              className='self-start sm:self-auto'
            />
          </div>
        )}
        {!apiKey && !is_loading && !gate.locked && (
          <button
            type='button'
            className='relative mt-2 block w-full rounded-lg border-2 border-dashed border-gray-300 p-4 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            onClick={createApiKey}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='mx-auto h-8 w-8 text-gray-400'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4'
              />
            </svg>
            <span className='mt-2 block text-sm font-medium italic text-gray-600'>
              {t(TRANSLATION_LOCATION + 'create_api_key_description')}
            </span>
          </button>
        )}
        {is_loading && (
          <div className='flex h-16 items-center justify-center rounded-md'>
            <ActivityIndicator className='' />
          </div>
        )}
      </div>
    </SettingsContainer>
  )
}

export default ApiKeys
